// components
import RandomPool from '../../../assets/js/components/random-pool';

// controllers
import AnimationController from '../../../assets/js/controller/animation';
import CursorController from '../../../assets/js/controller/cursor';

// helpers
import { bindEvents } from '../../../assets/js/helpers';

// sub-classes
import LoaderPageCharacter from './character';

// selecto
const selector = 'loader-page';

/*

JSON Schema:

{
	"id": "",

	"image": {
		"url": "",
		// aspectRatio: square, squareSmall, portrait, landscape
		"aspectRatio": ""
	},

	// animation: false or an object
	"animation": {
		"frames": 4
	},

	// featured: true, false (to take priority positioning and ~150% scale)
	"featured": false
}

*/

export default class LoaderPage {

	constructor(section) {
		this.el = {
			section,
			assets: section.querySelectorAll(`.${selector}__assets img`),

			assetsHorizontal: section.querySelectorAll(`.${selector}__assets img.${selector}__image-horizontal`),
			assetsVertical: section.querySelectorAll(`.${selector}__assets img.${selector}__image-vertical`),
			assetsSquare: section.querySelectorAll(`.${selector}__assets img.${selector}__image-square`),
			assetsSquareSmall: section.querySelectorAll(`.${selector}__assets img.${selector}__image-square-small`),

			imagesWrapper: section.querySelector(`.${selector}__images-wrapper`)
		};

		// class variable groups

		this.config = {
			transitions: [
				{
					// phase 0: intro
					duration: 1200, // msec,
					ratio: 0,
					increment: 0.66
				},
				{
					// phase 1: pause
					duration: 400, // msec
					ratio: 0.66,
					increment: 0.12
				},
				{
					// phase 2: waiting for page to load
					duration: 5000, // max delay, msec
					ratio: 0.78,
					increment: 0.42
				},
				{
					// final phase: outro
					duration: 700, // msec
					ratio: -1, // calculated dynamically
					increment: -1 // calculated dynamically
				}
			]
		};

		this.debug = {
			cursorTest: false
		};

		this.controllers = {
			animation: null
		};

		this.flags = {
			isListening: false,
			isOut: true,
			isTransitionComplete: false
		};

		this.patterns = [
			`	v,	-,	-,	-,	-,	-,	-,	-,	v,	-,	-,	-
				v,	s,	-,	-,	v,	S,	S,	-,	v,	s,	h,	h
				-,	-,	S,	S,	v,	S,	S,	h,	h,	S,	S,	v
				h,	h,	S,	S,	h,	h,	s,	-,	v,	S,	S,	v
				-,	s,	-,	s,	-,	S,	S,	-,	v,	s,	h,	h
				h,	h,	S,	S,	s,	S,	S,	h,	h,	S,	S,	v
				v,	s,	S,	S,	h,	h,	s,	v,	v,	S,	S,	v
				v,	-,	h,	h,	v,	S,	S,	-,	v,	s,	h,	h
				h,	h,	S,	S,	v,	S,	S,	h,	h,	S,	S,	v
				v,	s,	S,	S,	-,	-,	s,	v,	-,	S,	S,	v
				v,	-,	h,	h,	v,	S,	S,	v,	-,	s,	h,	h
				h,	h,	S,	S,	v,	S,	S,	h,	h,	S,	S,	v
				-,	s,	S,	S,	h,	h,	s,	v,	-,	S,	S,	v
				-,	-,	-,	-,	-,	-,	-,	v,	-,	-,	-,	-
			`
		];

		this.states = {
			transition: {
				start: 0,
				end: 0,
				duration: 0,
				phase: 0,
				resolve: null // function - TODO: this feels bad
			}
		};

		// class variables
		this.characters = [];

		// bind events to class scope
		this.events = bindEvents(this);

		// mosey along
		this.init();
	}

	static get selector() {
		return selector;
	}

	//

	init() {
		// this.show();
	}

	// event listeners

	addEventListeners() {
		if (!this.flags.isListening) {
			this.flags.isListening = true;
			this.controllers.animation = AnimationController.set({
				update: this.events._onAnimationUpdate,
				render: this.events._onAnimationRender
			});
		}
	}

	removeEventListeners() {
		if (this.flags.isListening) {
			this.flags.isListening = false;
			AnimationController.clear(this.controllers.animation);
		}
	}

	//

	_onAnimationUpdate() {
		return true;
	}

	_onAnimationRender() {

		// CURSOR TESTING

		if (this.debug.cursorTest === true) {

			const winWidth = window.innerWidth;
			const cursorRatioX = CursorController.coords.clientX / winWidth;

			for (let i = 0; i < this.characters.length; i++) {
				const character = this.characters[i];
				character.updateProgress(cursorRatioX * 2);
			}
		}

		// END CURSOR TESTING

		const phaseIndexMax = this.config.transitions.length - 1;

		let ratioBase = this.config.transitions[this.states.transition.phase].ratio;
		let ratioIncrement = this.config.transitions[this.states.transition.phase].increment;

		// phase logic

		const getRatio = () => ratioBase + ratioIncrement * Math.min(1, (Date.now() - this.states.transition.start) / this.states.transition.duration);
		let timeRatio = getRatio();

		const isNextPhase = () => {
			const curTransition = this.config.transitions[this.states.transition.phase];
			return (timeRatio >= curTransition.ratio + curTransition.increment);
		};

		const isNextPhaseLast = () => this.flags.isOut && this.states.transition.phase === phaseIndexMax - 1;

		const changePhase = (phase) => {
			this.states.transition.phase = phase;

			ratioBase = this.config.transitions[phase].ratio;
			ratioIncrement = this.config.transitions[phase].increment;

			this.states.transition.start = Date.now();
			this.states.transition.end = Date.now() + this.config.transitions[phase].duration;
			this.states.transition.duration = this.config.transitions[phase].duration;
			timeRatio = getRatio();
		};

		// test phase progress

		this.flags.isTransitionComplete = false;

		// phase changing (when completing previous phases)

		if (isNextPhaseLast() || isNextPhase()) {
			if (this.states.transition.phase === phaseIndexMax - 1) {
				if (this.flags.isOut) {
					this.config.transitions[phaseIndexMax].ratio = timeRatio;
					this.config.transitions[phaseIndexMax].increment = (2 - timeRatio);
					this.fadeOut();
					changePhase(phaseIndexMax);
				}
			} else if (this.states.transition.phase < phaseIndexMax) {
				changePhase(this.states.transition.phase + 1);
			}
		}

		// detect when transition completes

		if (this.states.transition.phase >= phaseIndexMax && this.flags.isOut && timeRatio >= ratioBase + ratioIncrement) {
			this.transitionComplete();
		}

		// update character positions

		for (let i = 0; i < this.characters.length; i++) {
			const character = this.characters[i];
			character.updateProgress(timeRatio);
		}
	}

	//

	rebuild() {
		const cleanPattern = this.patterns[0].replace(/(\r\n|\r|\n)/g, '.').replace(/(\s|\t|\s\t)/g, '');
		const patternRows = cleanPattern.split('.');
		const pattern = [];

		for (let i = 0; i < patternRows.length; i++) {
			const row = patternRows[i];
			pattern.push(row.split(','));
		}

		// clean pattern to prep for organizing images

		for (let y = 0; y < pattern.length; y++) {
			const row = pattern[y];
			for (let x = 0; x < row.length; x++) {
				const item = row[x];
				if (item === 'v') {
					pattern[y + 1][x] = '+';
				} else if (item === 'h') {
					pattern[y][x + 1] = '+';
				} else if (item === 'S') {
					pattern[y][x + 1] = '+';
					pattern[y + 1][x] = '+';
					pattern[y + 1][x + 1] = '+';
				}
			}
		}

		// randomize and rebuild characters

		const randomAssets = {
			horizontal: new RandomPool([...this.el.assetsHorizontal]),
			vertical: new RandomPool([...this.el.assetsVertical]),
			square: new RandomPool([...this.el.assetsSquare]),
			squareSmall: new RandomPool([...this.el.assetsSquareSmall])
		};

		// arrange images

		let htmlOutput = '';

		for (let y = 0; y < pattern.length; y++) {
			const row = pattern[y];
			for (let x = 0; x < row.length; x++) {
				const item = row[x];
				if (item === 'v') {
					const src = randomAssets.vertical.getItem().getAttribute('src');
					htmlOutput += `<img src="${src}" class="${selector}__image-vertical offset-x-${x} offset-y-${y}" data-aspect-ratio="vertical" />`;
				} else if (item === 'h') {
					const src = randomAssets.horizontal.getItem().getAttribute('src');
					htmlOutput += `<img src="${src}" class="${selector}__image-horizontal offset-x-${x} offset-y-${y}" data-aspect-ratio="horizontal" />`;
				} else if (item === 's') {
					const src = randomAssets.squareSmall.getItem().getAttribute('src');
					htmlOutput += `<img src="${src}" class="${selector}__image-square-small offset-x-${x} offset-y-${y}" data-aspect-ratio="squareSmall" />`;
				} else if (item === 'S') {
					const $item = randomAssets.square.getItem();
					const src = $item.getAttribute('src');
					if ($item.dataset.animated === 'true') {
						htmlOutput += `<div class="${selector}__image-square ${selector}__image-ss-wrapper offset-x-${x} offset-y-${y}" data-aspect-ratio="square"><img src="${src}" /></div>`;
					} else {
						htmlOutput += `<img src="${src}" class="${selector}__image-square offset-x-${x} offset-y-${y}" data-aspect-ratio="square" />`;
					}
				}
			}
		}

		this.el.imagesWrapper.innerHTML = htmlOutput;

		// assign characters

		AnimationController.one({
			update: () => true,
			render: () => {
				this.removeCharacters();
				this.configCharacters();
			}
		});
	}

	//

	removeCharacters() {
		for (let i = 0; i < this.characters.length; i++) {
			const character = this.characters[i];
			character.destroy();
		}

		this.characters = [];
	}

	configCharacters() {
		const $characters = this.el.imagesWrapper.children;
		for (let i = 0; i < $characters.length; i++) {
			const $character = $characters[i];
			const character = new LoaderPageCharacter($character);
			this.characters.push(character);
		}
	}

	//

	transitionIn() {
		this.states.transition.phase = 0;
		this.states.transition.start = Date.now();
		this.states.transition.end = this.states.transition.start + this.config.transitions[0].duration;
		this.states.transition.duration = this.config.transitions[0].duration;
	}

	transitionOut() {

	}

	transitionComplete() {
		this.removeEventListeners();
		this.flags.isTransitionComplete = true;
		this.checkResolve();
	}

	//

	show(isFirst = false) {
		return new Promise((resolve, reject) => {
			if (this.flags.isOut) {
				this.flags.isOut = false;

				// randomize characters
				this.rebuild();

				// animate characters
				this.addEventListeners();

				// show characters
				this.transitionIn();

				// show screen
				this.el.section.classList.remove('is-out', 'is-out-anim');
				this.el.section.classList.add('is-in');

				if (isFirst) {
					this.el.section.classList.add('is-in-first');
				}

				resolve();
			} else {
				reject();
			}
		});
	}

	hide() {
		return new Promise((resolve, reject) => {
			if (!this.flags.isOut) {
				this.flags.isOut = true;
				this.states.transition.resolve = resolve;
				this.checkResolve();
			} else {
				reject();
			}
		});
	}

	fadeOut() {
		this.el.section.classList.remove('is-in', 'is-in-first');
		this.el.section.classList.add('is-out-anim');
	}

	checkResolve() {
		if (this.flags.isTransitionComplete && this.flags.isOut) {
			this.fadeOut();
			this.states.transition.resolve();

			setTimeout(() => {
				this.el.section.classList.add('is-out');
				this.el.section.classList.remove('is-out-anim');
			}, 400);
		}
	}

	destroy() {
		removeEventListeners();
	}

}
